import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getServices, createAnonymousBooking } from '../services/api';
//import { createBooking, createPayment } from '../services/api';
import { useMerchant } from '../contexts/MerchantContext';
import { format,  startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isBefore } from 'date-fns';
//import { addDays } from 'date-fns';

function BookingForm() {
  const { serviceId, merchantSlug } = useParams();
  const navigate = useNavigate();
  //const { merchant } = useMerchant();
  const [service, setService] = useState(null);
  const [step, setStep] = useState(1); // 1: date/time, 2: user info
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const timeSlots = [
    '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'
  ];

  const unavailableSlots = ['13:00', '15:00']; // Mock unavailable times

  useEffect(() => {
    const fetchService = async () => {
      try {
        const response = await getServices();
        // Handle paginated response - extract results array
        const services = response.data.results || response.data;
        const selectedService = services.find(s => s.id === parseInt(serviceId));
        setService(selectedService);
      } catch (error) {
        console.error('Error fetching service:', error);
      }
    };
    fetchService();
  }, [serviceId]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const bookingResponse = await createAnonymousBooking({
        ...formData,
        service: serviceId,
        booking_date: format(selectedDate, 'yyyy-MM-dd'),
        booking_time: selectedTime + ':00' // 確保時間格式正確
      }, merchantSlug);

      // Navigate to payment page
      navigate(`/${merchantSlug}/payment/${bookingResponse.data.booking.id}`);
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('體驗登記失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

    const previousMonth = () => {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    };

    const nextMonth = () => {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
    };

    return (
      <div className="flex min-w-72 max-w-[336px] flex-1 flex-col gap-0.5">
        <div className="flex items-center p-1 justify-between">
          <button onClick={previousMonth} type="button">
            <div className="text-slate-800 dark:text-white flex size-10 items-center justify-center">
              <span className="material-symbols-outlined">chevron_left</span>
            </div>
          </button>
          <p className="text-slate-800 dark:text-white text-base font-bold leading-tight flex-1 text-center">
            {format(currentDate, 'yyyy年M月')}
          </p>
          <button onClick={nextMonth} type="button">
            <div className="text-slate-800 dark:text-white flex size-10 items-center justify-center">
              <span className="material-symbols-outlined">chevron_right</span>
            </div>
          </button>
        </div>
        
        <div className="grid grid-cols-7">
          {['日', '一', '二', '三', '四', '五', '六'].map(day => (
            <p key={day} className="text-slate-800 dark:text-white text-[13px] font-bold leading-normal tracking-[0.015em] flex h-12 w-full items-center justify-center pb-0.5">
              {day}
            </p>
          ))}
          
          {/* Empty cells for days before month start */}
          {Array.from({ length: monthStart.getDay() }, (_, i) => (
            <div key={`empty-${i}`} className="h-12 w-full"></div>
          ))}
          
          {days.map(day => {
            const isSelected = selectedDate && format(day, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
            const isPast = isBefore(day, new Date()) && !isToday(day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            
            return (
              <button
                key={format(day, 'yyyy-MM-dd')}
                type="button"
                onClick={() => !isPast && setSelectedDate(day)}
                disabled={isPast}
                className={`h-12 w-full text-sm font-medium leading-normal ${
                  isPast 
                    ? 'text-slate-400 dark:text-slate-500 cursor-not-allowed' 
                    : isSelected
                    ? 'text-white'
                    : isCurrentMonth
                    ? 'text-slate-800 dark:text-white'
                    : 'text-slate-400 dark:text-slate-500'
                }`}
              >
                <div className={`flex size-full items-center justify-center rounded-full ${
                  isSelected 
                    ? 'bg-primary' 
                    : !isPast && isCurrentMonth
                    ? 'hover:bg-primary/20'
                    : ''
                }`}>
                  {format(day, 'd')}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  if (!service) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-text-primary text-lg">載入中...</div>
      </div>
    );
  }

  if (step === 1) {
    return (
      <>
        {/* Header */}
        <header className="flex items-center justify-between whitespace-nowrap px-4 py-3">
          <Link to={`/${merchantSlug}`} className="flex items-center gap-2 text-text-primary">
            <span className="material-symbols-outlined">arrow_back</span>
            <span className="text-lg font-medium">返回</span>
          </Link>
        </header>

        <div className="flex flex-wrap justify-between gap-3 p-4">
          <p className="text-slate-800 dark:text-white text-4xl font-black leading-tight tracking-[-0.033em] min-w-72">
            {service.name}
          </p>
        </div>

        <div className="flex flex-col md:flex-row flex-wrap items-start justify-center gap-6 p-4">
          {renderCalendar()}
          
          <div className="flex flex-1 flex-col gap-4 p-4 min-w-72">
            <p className="text-slate-800 dark:text-white font-bold">選擇時間</p>
            <div className="flex gap-3 flex-wrap">
              {timeSlots.map(time => {
                const isUnavailable = unavailableSlots.includes(time);
                const isSelected = selectedTime === time;
                
                return (
                  <button
                    key={time}
                    type="button"
                    onClick={() => !isUnavailable && setSelectedTime(time)}
                    disabled={isUnavailable}
                    className={`flex h-10 shrink-0 items-center justify-center gap-x-2 rounded-lg px-4 ${
                      isUnavailable
                        ? 'bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 cursor-not-allowed'
                        : isSelected
                        ? 'bg-primary border border-primary'
                        : 'bg-white dark:bg-background-dark border border-slate-300 dark:border-slate-700 hover:border-primary hover:text-primary dark:hover:border-primary'
                    }`}
                  >
                    <p className={`text-sm font-medium leading-normal ${
                      isUnavailable
                        ? 'text-slate-400 dark:text-slate-500'
                        : isSelected
                        ? 'text-white'
                        : ''
                    }`}>
                      {time} - {String(parseInt(time.split(':')[0]) + 1).padStart(2, '0')}:00
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex justify-stretch mt-auto">
          <div className="flex flex-1 gap-3 flex-wrap px-4 py-3 justify-between">
            <Link 
              to={`/${merchantSlug}`}
              className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-5 bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-white text-base font-bold leading-normal tracking-[0.015em]"
            >
              <span className="truncate">返回</span>
            </Link>
            <button
              type="button"
              onClick={() => selectedDate && selectedTime && setStep(2)}
              disabled={!selectedDate || !selectedTime}
              className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-5 bg-primary text-white text-base font-bold leading-normal tracking-[0.015em] disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <span className="truncate">下一步</span>
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Header */}
      <header className="flex items-center justify-between whitespace-nowrap px-4 py-3">
        <button 
          onClick={() => setStep(1)}
          className="flex items-center gap-2 text-text-primary"
        >
          <span className="material-symbols-outlined">arrow_back</span>
          <span className="text-lg font-medium">返回</span>
        </button>
        <h1 className="text-2xl font-bold tracking-tighter text-text-light dark:text-text-dark">體驗服務</h1>
      </header>

      <main className="flex flex-col gap-8">
        {/* Service Summary */}
        <div className="flex flex-col gap-4 px-4">
          <div className="flex items-center gap-4 bg-white dark:bg-background-dark rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="text-primary flex items-center justify-center rounded-full bg-primary/10 shrink-0 size-12">
              <span className="material-symbols-outlined !text-2xl">calendar_month</span>
            </div>
            <div className="flex flex-col justify-center">
              <p className="text-text-light dark:text-text-dark text-lg font-semibold leading-normal line-clamp-1">
                {service.name}
              </p>
              <p className="text-text-accent-light dark:text-text-accent-dark text-base font-normal leading-normal line-clamp-2">
                {format(selectedDate, 'yyyy年M月d日')} {selectedTime}
              </p>
            </div>
          </div>
        </div>

        {/* User Info Form */}
        <div className="flex flex-col gap-6">
          <h2 className="text-text-light dark:text-text-dark text-2xl font-bold leading-tight tracking-tight px-4">
            填寫聯絡資訊
          </h2>
          
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-4">
              <label className="flex flex-col flex-1">
                <p className="text-text-accent-light dark:text-text-accent-dark text-base font-medium leading-normal pb-2">
                  姓名 *
                </p>
                <input
                  className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50 border border-[#dbe2e6] dark:border-gray-700 bg-white dark:bg-gray-800 focus:border-primary h-14 placeholder:text-text-accent-light dark:placeholder:text-text-accent-dark p-[15px] text-base font-normal leading-normal"
                  placeholder="請輸入您的姓名"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  required
                />
              </label>
              
              <label className="flex flex-col flex-1">
                <p className="text-text-accent-light dark:text-text-accent-dark text-base font-medium leading-normal pb-2">
                  聯絡電話 *
                </p>
                <input
                  className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50 border border-[#dbe2e6] dark:border-gray-700 bg-white dark:bg-gray-800 focus:border-primary h-14 placeholder:text-text-accent-light dark:placeholder:text-text-accent-dark p-[15px] text-base font-normal leading-normal"
                  placeholder="請輸入您的聯絡電話"
                  name="customer_phone"
                  value={formData.customer_phone}
                  onChange={handleChange}
                  required
                />
              </label>
            </div>

            <div className="px-4">
              <label className="flex flex-col flex-1">
                <p className="text-text-accent-light dark:text-text-accent-dark text-base font-medium leading-normal pb-2">
                  電子郵件 *
                </p>
                <input
                  className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50 border border-[#dbe2e6] dark:border-gray-700 bg-white dark:bg-gray-800 focus:border-primary h-14 placeholder:text-text-accent-light dark:placeholder:text-text-accent-dark p-[15px] text-base font-normal leading-normal"
                  placeholder="請輸入您的電子郵件"
                  type="email"
                  name="customer_email"
                  value={formData.customer_email}
                  onChange={handleChange}
                  required
                />
              </label>
            </div>

            <div className="px-4">
              <label className="flex flex-col flex-1">
                <p className="text-text-accent-light dark:text-text-accent-dark text-base font-medium leading-normal pb-2">
                  備註
                </p>
                <textarea
                  className="form-textarea flex w-full min-w-0 flex-1 resize-y overflow-hidden rounded-lg text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50 border border-[#dbe2e6] dark:border-gray-700 bg-white dark:bg-gray-800 focus:border-primary h-32 placeholder:text-text-accent-light dark:placeholder:text-text-accent-dark p-[15px] text-base font-normal leading-normal"
                  placeholder="如果您有任何特殊需求，請在此說明"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                />
              </label>
            </div>

            <div className="px-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full h-14 flex items-center justify-center bg-primary text-white text-lg font-bold rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
              >
                {loading ? '處理中...' : '提交體驗登記'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </>
  );
}

export default BookingForm;